// Copyright 2021 Rennie Glen Software LLC
import { Param } from "./Param";
import { pageRangeErrorIndex } from "./CommonGlobals";
import { multiReplace } from "./StrUtil";
import { Translator } from "./Translator";

// Returns a 
export type ValidateText<T> = (text: string) => Promise<T>;

export interface TextParam<T> extends Param<T>  {
    text: string;
    readonly error: string;
}

export type TextParamErrorCode =
    "notEmpty"
    | "greaterThanZero"
    |  "atLeastZero" 
    | "aNumber" 
    | "atLeastOne" 
    | "aWholeNumber"
    | "powerOfTwo"
    | "betweenZeroAndOneHundred";

export interface TextParamError {
    kind: "TextParamError";
    code: TextParamErrorCode;
}
export interface DuplicatePaperNameError {
    kind: "TextParamError";
    code: "duplicatePaperSizeName";
    name: string;
}

export interface ParsePageRangeError {
    kind: "TextParamError";
    code: "badRange";
    text: string;  // The text that was being parsed.
    pos: number;   // The position where the parser choked.
}

export type AnyTextParamError = TextParamError | DuplicatePaperNameError | ParsePageRangeError;

function newError(code: TextParamErrorCode): TextParamError {
    return {
        kind: "TextParamError",
        code: code
    };
}

export function newDuplicatePaperNameError(name: string): DuplicatePaperNameError {
    return {
        kind: "TextParamError",
        code: "duplicatePaperSizeName",
        name
    };
}

export class TextParamWithType<T> implements Param<T> {
    _text: string;  // Should be private, but volar chokes.
    error: any;
    _validate: ValidateText<T>;

    constructor(validate: ValidateText<T>, text = "") {
        this._validate = validate;
        this._text = text;
        this.error = "";
    }


    copyFrom(param: Param<T>): void {
        const thisType = typeof(this);
        const thatType = typeof(param);
        const that = param as TextParamWithType<T>;
        if (thisType === thatType) {
            this._validate = that._validate;
            this._text = that._text;
            this.error = that.error;
        } else {
            throw new Error(`Can't copy from ${thatType} to ${thisType}.`);
        }
    }

    get text() { return this._text; }

    set text(text: string) {
        this._text = text;
        this._validate(text).catch(e => this.error = e).then(this.error = null);
    }

    validate(): Promise<T> {
        return this._validate(this.text);
    }

    loadValid(obj: any): void {
        this._text = String(obj);
    }

    pack() { return this.text; }
    unpack(x: any) { this.text = x.toString(); }
}


export function newTextParam(text: string): TextParamWithType<string> {
    const validate = (x: string) => Promise.resolve(x);
    return new TextParamWithType<string>(validate, text);
}    


export function newNonEmptyTextParam(text: string): TextParamWithType<string> {
    const validate = async (x: string) => {
        if (0 == x.trim().length) {
            throw newError("notEmpty");
        } else {
            return x;
        }
    }
    return new TextParamWithType<string>(validate, text);

}

export function newPositiveNumberParam(text: string): TextParamWithType<number> {
    const validate = async (x: string) => {
        const n = Number(x);
        if (n > 0)
            return n;
        else
            throw newError("greaterThanZero");
    }
    return new TextParamWithType<number>(validate, text);
}

export function newNonNegativeNumberParam(text: string): TextParamWithType<number> {
    const validate = async (x: string) => {
        const n = Number(x);
        if (n >= 0)
            return n;
        else
            throw newError("greaterThanZero");
    }
    return new TextParamWithType<number>(validate, text);
}

export function newPowerOfTwoParam(text: string): TextParamWithType<number> {
    const validate = async (x: string) => {
        const n = Number(x);
        if ([2, 4, 8, 16, 32, 64, 128, 256].includes(n))
            return n;
        else
            throw newError("powerOfTwo");
    }
    return new TextParamWithType<number>(validate, text);
}

export function newNumberParam(text: string): TextParamWithType<number> {
    const validate = async (x: string) => {
        const n = Number(x);
        if (isNaN(n))
            throw newError("aNumber");
        else
            return n;
    }
    return new TextParamWithType<number>(validate, text);
}

export function newNaturalNumberParam(text: string): TextParamWithType<number> {
    const validate = async (x: string) => {
        const n = Number(x);
        if (isNaN(n))
            throw newError("aNumber");
        else if (n < 1)
            throw newError("atLeastOne");
        else if (!Number.isInteger(n))
            throw newError("aWholeNumber");
        else
            return n;
    }
    return new TextParamWithType<number>(validate, text);
}

export function newWholeNumberParam(text: string): TextParamWithType<number> {
    const validate = async (x: string) => {
        const n = Number(x);
        if (isNaN(n))
            throw newError("aNumber");
        else if (n < 0)
            throw newError("atLeastZero");
        else if (!Number.isInteger(n))
            throw newError("aWholeNumber");
        else
            return n;
    }
    return new TextParamWithType<number>(validate, text);
}

export function newOneHundredNumberParam(text: string): TextParamWithType<number> {
    const validate = async (x: string) => {
        const n = Number(x);
        if (isNaN(n))
            throw newError("aNumber");
        else if (n < 0 || n > 100)
            throw newError("betweenZeroAndOneHundred");
        else if (!Number.isInteger(n))
            throw newError("aWholeNumber");
        else
            return n;
    }
    return new TextParamWithType<number>(validate, text);
}

// Had I understood typescript better, I wouldn't have needed this type at all
// and instead passed a Vue.$t directly.
export type PageRangeTerms = [string, string][];
export function termsFrom(t: Translator): PageRangeTerms {
    return [
        [t("range.all").toString(), "all"],
        [t("range.first").toString(), "first"],
        [t("range.last").toString(), "last"],
        [t("range.odd").toString(), "odd"],
        [t("range.even").toString(), "even"],
        [t("range.group").toString(), "group"],
    ];  
}

export function newPageRangeParam(text: string, pageRangeTerms: PageRangeTerms
    ): TextParamWithType<string>
{
    const validate = async (x: string) => {
        x = multiReplace(x, new Map(pageRangeTerms));
        const badIndex = await pageRangeErrorIndex(x);
        if (badIndex < 0) {
            return x;
        } else {
            const err: ParsePageRangeError = {
                kind: "TextParamError",
                code: "badRange",
                text: x,
                pos: badIndex
            }
            throw err;
        }
    };
    return new TextParamWithType<string>(validate, text);
}

export function newEnumParam(enurn: any, defaultValue?: string): TextParamWithType<string> {
    const validate = (x: string) => {
        return Promise.resolve(x);
    };
    const defValue = defaultValue ?? Object.values(enurn)[0] as string;
    return new TextParamWithType<string>(validate, defValue);
}

/// Used for item counts in GangDlg.
export class ParamsArray<V, P extends Param<V>> implements Param<V[]> {
    newParam: () => P;
    constructor(newParam: () => P) {
        this.newParam = newParam;
    }
    array: P[] = [];
    copyFrom(param: Param<V[]>): void {
        const thisType = typeof(this);
        const thatType = typeof(param);
        const that = param as unknown as ParamsArray<V, P>;
        if (thisType === thatType) {
            const arrayCopy = that.array.map(param => {
                const copy = this.newParam();
                copy.copyFrom(param);
                return copy;
            });
            this.array.splice(0, this.array.length, ...arrayCopy);
        } else {
            throw new Error(`Can't copy from ${thatType} to ${thisType}.`);
        }
    }
    pack(): string[] {
        return this.array.map(param => param.pack());
    }
    unpack(objs: any): void {
        const newArray = [];
        for (const obj of (objs as any[])) {
            const param = this.newParam();
            param.unpack(obj);
            newArray.push(param);
        }
        this.array = newArray;
    }
    validate() {
        const promises = this.array.map(param => param.validate());
        return Promise.all(promises);
    }
    loadValid(objs: any): void {
        const newArray = [];
        for (const obj of (objs as any[])) {
            const param = this.newParam();
            param.loadValid(obj);
            newArray.push(param);
        }
        this.array = newArray;
    }

    fillToSize(size: number): void {
        while (this.array.length < size) {
            this.array.push(this.newParam());
        }
    }

    newParams(count: number): P[] {
        return Array.from({length: count}, this.newParam);
    }
}

